import { useEffect, useState } from 'react'
import { Plus, Copy, Trash2, Palette } from 'lucide-react'
import { listThemes, createTheme, updateTheme, deleteTheme } from './reportThemesApi'
import { ThemeEditor } from './ThemeEditor'
import { ThemePreview } from './ThemePreview'
import type { ReportTheme, ReportThemePatch } from '../../types/reportTheme'

const EDITABLE_FIELDS = [
  'name',
  'isDefault',
  'primaryColor',
  'secondaryColor',
  'fontFamily',
  'chartPalette',
  'clientLogoUrl',
  'agencyLogoUrl',
  'cover',
  'header',
  'footer',
  'contact',
  'intro',
] as const

function pickEditableFields(theme: ReportTheme): ReportThemePatch {
  const patch: Record<string, unknown> = {}
  for (const field of EDITABLE_FIELDS) {
    patch[field] = theme[field]
  }
  return patch as ReportThemePatch
}

export function ThemesManager({
  activeThemeId,
  onAssign,
}: {
  activeThemeId?: number | null
  onAssign?: (themeId: number | null) => void
} = {}) {
  const [themes, setThemes] = useState<ReportTheme[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<ReportTheme | null>(null)
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listThemes()
      .then((list) => {
        if (cancelled) return
        setThemes(list)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  function handleSelect(theme: ReportTheme) {
    setSelected(theme)
    setConfirmingDeleteId(null)
  }

  async function handleEditorChange(patch: ReportThemePatch) {
    if (!selected) return
    const updatedLocal = { ...selected, ...patch } as ReportTheme
    setSelected(updatedLocal)
    setThemes((prev) => prev.map((t) => (t.id === selected.id ? updatedLocal : t)))
    await updateTheme(selected.id, patch)
  }

  async function handleCreate() {
    const created = await createTheme({ name: 'Nouveau thème', cover: { enabled: true } })
    setThemes((prev) => [...prev, created])
    setSelected(created)
    setConfirmingDeleteId(null)
  }

  async function handleDuplicate(theme: ReportTheme) {
    const patch = pickEditableFields(theme)
    const created = await createTheme({ ...patch, name: `${theme.name} (copie)`, isDefault: false })
    setThemes((prev) => [...prev, created])
    setSelected(created)
    setConfirmingDeleteId(null)
  }

  function handleDeleteClick(id: number) {
    setConfirmingDeleteId(id)
  }

  async function handleConfirmDelete(id: number) {
    await deleteTheme(id)
    setThemes((prev) => prev.filter((t) => t.id !== id))
    setSelected((prev) => (prev && prev.id === id ? null : prev))
    setConfirmingDeleteId(null)
  }

  return (
    <div className="flex h-full min-h-0 bg-light-body">
      {/* Rail gauche — bibliothèque */}
      <aside className="flex w-72 shrink-0 flex-col border-r border-light-border bg-light-sidebar">
        <div className="flex items-center justify-between px-4 pb-3 pt-4">
          <div>
            <h1 className="text-sm font-semibold text-[#1B2559]">Thèmes de rapport</h1>
            <p className="text-xs text-[#8B8FA7]">{themes.length} thème{themes.length > 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="px-4 pb-3">
          <button
            type="button"
            onClick={handleCreate}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-orion-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-orion-primary-hover"
          >
            <Plus size={16} strokeWidth={2} />
            Nouveau thème
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 pb-4">
          {loading ? (
            <p className="px-1 py-2 text-sm text-[#8B8FA7]">Chargement…</p>
          ) : themes.length === 0 ? (
            <div className="mt-6 flex flex-col items-center gap-2 px-4 text-center">
              <Palette size={22} className="text-[#C8CBD8]" strokeWidth={1.5} />
              <p className="text-sm font-medium text-[#1B2559]">Aucun thème</p>
              <p className="text-xs text-[#8B8FA7]">Crée ton premier thème de rapport pour brander tes livrables.</p>
            </div>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {themes.map((theme) => {
                const active = selected?.id === theme.id
                const confirming = confirmingDeleteId === theme.id
                const isAssigned = activeThemeId != null && theme.id === activeThemeId
                return (
                  <li
                    key={theme.id}
                    className={`group rounded-lg border transition-colors ${
                      active
                        ? 'border-orion-primary bg-orion-primary-soft'
                        : 'border-transparent hover:border-light-border hover:bg-light-surface-hover'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleSelect(theme)}
                      className="flex w-full items-center gap-3 px-2.5 py-2 text-left"
                    >
                      <span
                        className="flex h-8 w-8 shrink-0 overflow-hidden rounded-md border border-light-border shadow-sm"
                        aria-hidden
                      >
                        <span className="h-full w-1/2" style={{ backgroundColor: theme.primaryColor }} />
                        <span className="h-full w-1/2" style={{ backgroundColor: theme.secondaryColor }} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          <span className="block truncate text-sm font-medium text-[#1B2559]">{theme.name}</span>
                          {isAssigned ? (
                            <span className="shrink-0 rounded-full bg-orion-primary-soft px-1.5 py-0.5 text-[10px] font-semibold text-orion-primary">
                              Assigné
                            </span>
                          ) : null}
                        </span>
                        <span className="block text-[11px] capitalize text-[#8B8FA7]">{theme.fontFamily}</span>
                      </span>
                    </button>

                    <div
                      className={`flex items-center gap-1 px-2.5 pb-2 ${
                        active || confirming ? '' : 'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100'
                      }`}
                    >
                      {onAssign ? (
                        <button
                          type="button"
                          onClick={() => onAssign(theme.id)}
                          className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-[#8B8FA7] hover:bg-white hover:text-orion-primary"
                        >
                          Choisir
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => handleDuplicate(theme)}
                        className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-[#8B8FA7] hover:bg-white hover:text-orion-primary"
                      >
                        <Copy size={12} strokeWidth={1.75} />
                        Dupliquer
                      </button>
                      {confirming ? (
                        <button
                          type="button"
                          onClick={() => handleConfirmDelete(theme.id)}
                          className="flex items-center gap-1 rounded-md bg-score-critical px-2 py-1 text-[11px] font-semibold text-white hover:opacity-90"
                        >
                          Confirmer
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(theme.id)}
                          className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-[#8B8FA7] hover:bg-white hover:text-score-critical"
                        >
                          <Trash2 size={12} strokeWidth={1.75} />
                          Supprimer
                        </button>
                      )}
                    </div>
                    {confirming ? (
                      <p className="px-2.5 pb-2 text-[11px] leading-snug text-score-critical">
                        Le thème sera retiré des rapports et clients qui l’utilisent.
                      </p>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </aside>

      {/* Panneau éditeur */}
      <section className="flex w-[360px] shrink-0 flex-col border-r border-light-border bg-light-surface">
        {selected ? (
          <>
            <div className="border-b border-light-border px-5 py-3">
              <p className="text-[11px] uppercase tracking-wider text-[#8B8FA7]">Édition</p>
              <h2 className="truncate text-sm font-semibold text-[#1B2559]">{selected.name}</h2>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-2">
              <ThemeEditor theme={selected} onChange={handleEditorChange} />
            </div>
          </>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 px-8 text-center">
            <Palette size={26} className="text-[#C8CBD8]" strokeWidth={1.5} />
            <p className="text-sm font-medium text-[#1B2559]">Sélectionne un thème</p>
            <p className="text-xs text-[#8B8FA7]">Choisis un thème à gauche ou crée-en un nouveau pour l’éditer.</p>
          </div>
        )}
      </section>

      {/* Aperçu live */}
      <section className="flex-1 overflow-y-auto bg-light-elevated">
        {selected ? (
          <div className="mx-auto max-w-[560px] px-6 py-8">
            <p className="mb-4 text-center text-[11px] uppercase tracking-wider text-[#8B8FA7]">Aperçu · exemple de rendu</p>
            <ThemePreview theme={selected} />
          </div>
        ) : (
          <div className="flex h-full items-center justify-center px-8 text-center">
            <p className="text-xs text-[#8B8FA7]">L’aperçu du thème s’affichera ici.</p>
          </div>
        )}
      </section>
    </div>
  )
}
